import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FiInfo, FiPlus, FiTrash2 } from "react-icons/fi";
import axios from "../../api/axiosInstance";
import { usePmsTheme } from "../../Components/PmsThemeContext";
import { useOnboardingFlow } from "../OnboardingFlowContext";
import { getOnboardingMessages } from "../onboardingI18n";
import "../OnboardingApp.css";

function createRow(seed = {}) {
  return {
    id: seed.id ?? null,
    client_id: seed.client_id || `draft-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    name: seed.name || "",
    description: seed.description || "",
    max_adults: seed.max_adults ?? 2,
    max_children: seed.max_children ?? 0,
    max_occupancy: seed.max_occupancy ?? "",
  };
}

function formatDate(dateValue) {
  if (!dateValue) return "Not applied yet";
  const parsed = new Date(dateValue);
  if (Number.isNaN(parsed.getTime())) return "Invalid date";
  return parsed.toLocaleString();
}

function FieldHelp({ text, ariaLabel }) {
  return (
    <span className="onboarding-helpWrap">
      <button
        type="button"
        className="onboarding-helpButton"
        aria-label={ariaLabel}
      >
        <FiInfo size={14} />
      </button>
      <span className="onboarding-helpTooltip" role="tooltip">
        {text}
      </span>
    </span>
  );
}

export default function RoomCategoriesQuestionnaire() {
  const { languageId, language } = usePmsTheme();
  const {
    clearStepCommandState,
    markStepComplete,
    markStepIncomplete,
    setStepCommandState,
    steps,
  } = useOnboardingFlow();
  const copy = getOnboardingMessages(languageId);
  const [rows, setRows] = useState([]);
  const [sessionMeta, setSessionMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [feedback, setFeedback] = useState({ type: "", message: "" });
  const stepNumber = useMemo(
    () => Math.max(steps.findIndex((step) => step.id === "room-categories") + 1, 1),
    [steps]
  );

  const loadDraft = useCallback(async () => {
    setLoading(true);
    setFeedback({ type: "", message: "" });

    try {
      const response = await axios.get("/api/onboarding/room-categories");
      const incomingRows = Array.isArray(response.data?.rows) ? response.data.rows : [];

      setRows(
        incomingRows.length
          ? incomingRows.map((row) =>
              createRow({
                ...row,
                client_id: `server-${row.id}`,
                max_occupancy: row.max_occupancy ?? "",
              })
            )
          : [createRow()]
      );
      setSessionMeta(response.data?.session || null);
      setIsDirty(false);

      if (incomingRows.length > 0) {
        markStepComplete("room-categories");
      } else {
        markStepIncomplete("room-categories");
      }
    } catch (error) {
      console.error("[Onboarding][RoomCategories] Error loading draft:", error);
      setRows([createRow()]);
      setIsDirty(false);
      setFeedback({
        type: "error",
        message: copy.roomCategories.loadError,
      });
    } finally {
      setLoading(false);
    }
  }, [copy.roomCategories.loadError, markStepComplete, markStepIncomplete]);

  useEffect(() => {
    loadDraft();
  }, [loadDraft]);

  const updateRow = (clientId, field, value) => {
    setIsDirty(true);
    setRows((currentRows) =>
      currentRows.map((row) => (row.client_id === clientId ? { ...row, [field]: value } : row))
    );
  };

  const addRow = () => {
    setIsDirty(true);
    setRows((currentRows) => [createRow(), ...currentRows]);
  };

  const removeRow = (clientId) => {
    setIsDirty(true);
    setRows((currentRows) => currentRows.filter((row) => row.client_id !== clientId));
  };

  const validateRows = useCallback(() => {
    for (let index = 0; index < rows.length; index += 1) {
      const row = rows[index];

      if (!String(row.name || "").trim()) {
        return copy.roomCategories.validationName(index + 1);
      }

      if (Number(row.max_adults) < 1) {
        return copy.roomCategories.validationAdults(index + 1);
      }

      if (Number(row.max_children) < 0) {
        return copy.roomCategories.validationChildren(index + 1);
      }

      if (row.max_occupancy !== "" && Number(row.max_occupancy) < Number(row.max_adults)) {
        return copy.roomCategories.validationOccupancy(index + 1);
      }
    }

    return null;
  }, [
    copy.roomCategories,
    rows,
  ]);

  const handleSave = useCallback(async () => {
    const validationError = validateRows();
    if (validationError) {
      setFeedback({ type: "error", message: validationError });
      return false;
    }

    setSaving(true);
    setFeedback({ type: "", message: "" });

    try {
      const payload = rows.map((row) => ({
        id: row.id || undefined,
        name: String(row.name || "").trim(),
        description: String(row.description || "").trim(),
        max_adults: Number(row.max_adults),
        max_children: Number(row.max_children),
        max_occupancy: row.max_occupancy === "" ? null : Number(row.max_occupancy),
      }));

      const response = await axios.put("/api/onboarding/room-categories", { rows: payload });
      const savedRows = Array.isArray(response.data?.rows) ? response.data.rows : [];

      setRows(
        savedRows.length
          ? savedRows.map((row) =>
              createRow({
                ...row,
                client_id: `server-${row.id}`,
                max_occupancy: row.max_occupancy ?? "",
              })
            )
          : []
      );
      setSessionMeta(response.data?.session || null);
      setIsDirty(false);

      if (savedRows.length > 0) {
        markStepComplete("room-categories");
      } else {
        markStepIncomplete("room-categories");
      }

      setFeedback({
        type: "success",
        message: copy.roomCategories.saveSuccess,
      });
      return true;
    } catch (error) {
      console.error("[Onboarding][RoomCategories] Error saving draft:", error);
      setFeedback({
        type: "error",
        message: error?.response?.data?.error || copy.roomCategories.saveError,
      });
      return false;
    } finally {
      setSaving(false);
    }
  }, [
    copy.roomCategories.saveError,
    copy.roomCategories.saveSuccess,
    markStepComplete,
    markStepIncomplete,
    rows,
    validateRows,
  ]);

  useEffect(() => {
    setStepCommandState("room-categories", {
      onSave: handleSave,
      canSave: !loading,
      canProceed: !isDirty,
      isDirty,
      isSaving: saving,
    });

    return () => {
      clearStepCommandState("room-categories");
    };
  }, [clearStepCommandState, handleSave, isDirty, loading, saving, setStepCommandState]);

  if (loading) {
    return (
      <div className="onboarding-emptyState">
        <h3 className="onboarding-sectionTitle">{copy.roomCategories.title}</h3>
        <p className="onboarding-sectionText">{copy.roomCategories.copy}</p>
      </div>
    );
  }

  return (
    <div className="onboarding-grid">
      <section className="onboarding-formCard">
        <div className="onboarding-formHeader">
          <div>
            <div className="onboarding-eyebrow">{copy.roomCategories.eyebrow}</div>
            <div className="onboarding-stepCount">{copy.progress.stepCounter(stepNumber, steps.length)}</div>
            <h3 className="onboarding-sectionTitle">{copy.roomCategories.title}</h3>
            <p className="onboarding-sectionText">{copy.roomCategories.copy}</p>
          </div>

          <div className="onboarding-formActions">
            <button type="button" className="onboarding-ghostBtn" onClick={addRow}>
              <FiPlus size={16} />
              {copy.roomCategories.add}
            </button>
          </div>
        </div>

        {rows.length === 0 ? (
          <div className="onboarding-emptyState">
            <h4 className="onboarding-sectionTitle">{copy.roomCategories.emptyTitle}</h4>
            <p className="onboarding-sectionText">{copy.roomCategories.emptyCopy}</p>
            <div className="onboarding-toolbar" style={{ justifyContent: "center" }}>
              <button type="button" className="onboarding-primaryBtn" onClick={addRow}>
                <FiPlus size={16} />
                {copy.roomCategories.add}
              </button>
            </div>
          </div>
        ) : (
          <div className="onboarding-rows">
            {rows.map((row, index) => (
              <article key={row.client_id} className="onboarding-rowCard">
                <div className="onboarding-rowHeader">
                  <div>
                    <h4>{copy.roomCategories.itemTitle(index + 1)}</h4>
                    <p className="onboarding-hint">{copy.roomCategories.itemCopy}</p>
                  </div>

                  <button
                    type="button"
                    className="onboarding-dangerBtn"
                    onClick={() => removeRow(row.client_id)}
                  >
                    <FiTrash2 size={16} />
                    {copy.roomCategories.remove}
                  </button>
                </div>

                <div className="onboarding-fields">
                  <label className="onboarding-field">
                    <span className="onboarding-label">{copy.roomCategories.name}</span>
                    <input
                      className="onboarding-input"
                      value={row.name}
                      onChange={(event) => updateRow(row.client_id, "name", event.target.value)}
                      placeholder={copy.roomCategories.namePlaceholder}
                    />
                  </label>

                  <label className="onboarding-field">
                    <span className="onboarding-label onboarding-labelWithHelp">
                      {copy.roomCategories.maxAdults}
                      <FieldHelp
                        text={copy.roomCategories.maxAdultsHelp}
                        ariaLabel={copy.roomCategories.maxAdultsHelpAria}
                      />
                    </span>
                    <input
                      className="onboarding-input"
                      type="number"
                      min="1"
                      value={row.max_adults}
                      onChange={(event) => updateRow(row.client_id, "max_adults", event.target.value)}
                    />
                  </label>

                  <label className="onboarding-field">
                    <span className="onboarding-label onboarding-labelWithHelp">
                      {copy.roomCategories.maxChildren}
                      <FieldHelp
                        text={copy.roomCategories.maxChildrenHelp}
                        ariaLabel={copy.roomCategories.maxChildrenHelpAria}
                      />
                    </span>
                    <input
                      className="onboarding-input"
                      type="number"
                      min="0"
                      value={row.max_children}
                      onChange={(event) => updateRow(row.client_id, "max_children", event.target.value)}
                    />
                  </label>

                  <label className="onboarding-field">
                    <span className="onboarding-label onboarding-labelWithHelp">
                      {copy.roomCategories.maxOccupancy}
                      <FieldHelp
                        text={copy.roomCategories.maxOccupancyHelp}
                        ariaLabel={copy.roomCategories.maxOccupancyHelpAria}
                      />
                    </span>
                    <input
                      className="onboarding-input"
                      type="number"
                      min="1"
                      value={row.max_occupancy}
                      onChange={(event) => updateRow(row.client_id, "max_occupancy", event.target.value)}
                      placeholder={copy.roomCategories.maxOccupancyPlaceholder}
                    />
                  </label>

                  <label className="onboarding-field onboarding-field--full">
                    <span className="onboarding-label">{copy.roomCategories.description}</span>
                    <textarea
                      className="onboarding-textarea"
                      value={row.description}
                      onChange={(event) => updateRow(row.client_id, "description", event.target.value)}
                      placeholder={copy.roomCategories.descriptionPlaceholder}
                    />
                  </label>
                </div>
              </article>
            ))}
          </div>
        )}

        {feedback.message ? (
          <div
            className={`onboarding-status ${
              feedback.type === "success" ? "onboarding-status--success" : "onboarding-status--error"
            }`}
          >
            {feedback.message}
          </div>
        ) : null}
      </section>

      <aside className="onboarding-infoCard">
        <div className="onboarding-eyebrow">{copy.roomCategories.eyebrow}</div>
        <h3 className="onboarding-sectionTitle">{copy.roomCategories.summaryTitle}</h3>
        <p className="onboarding-sectionText">{copy.roomCategories.summaryIntro}</p>

        <div className="onboarding-pointsList">
          {copy.roomCategories.summaryPoints.map((point) => (
            <div key={point} className="onboarding-pointItem">
              <span className="onboarding-pointDot" />
              <span>{point}</span>
            </div>
          ))}
        </div>

        <div className="onboarding-kpi">
          <span className="onboarding-kpiLabel">{copy.roomCategories.summaryCountLabel}</span>
          <span className="onboarding-kpiValue">{rows.length}</span>
        </div>
        <div className="onboarding-kpi">
          <span className="onboarding-kpiLabel">{copy.roomCategories.summaryLanguageLabel}</span>
          <span className="onboarding-kpiValue">{language?.nativeLabel || language?.label || "-"}</span>
        </div>
        <div className="onboarding-kpi">
          <span className="onboarding-kpiLabel">{copy.roomCategories.summarySavedLabel}</span>
          <span className="onboarding-kpiValue">
            {sessionMeta?.updated_at ? formatDate(sessionMeta.updated_at) : copy.roomCategories.summaryNotSaved}
          </span>
        </div>
      </aside>
    </div>
  );
}
